import React, { useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from 'src/components/Layout/Header';
import { Box } from 'src/components/UI/Box';
import { Button } from 'src/components/UI/Button';
import { ReactComponent as Allegril } from 'src/assets/mevo-pill/allegril.svg';
import { Flex } from 'src/components/UI/Flex';
import Typography from 'src/components/Typography';
import { DEFAULT_MEVO_WHATSAPP_NUMBER } from 'src/helpers/defaultValues';
import { openWhatsApp } from 'src/helpers/openWhatsApp';
import WhatsappIcon from 'src/components/Icons/WhatsappIcon';
import { openApp } from 'src/helpers/openApp';
import { Image } from 'src/components/UI/Image';
import { openAppStore, openPlayStore, verifyOSSystem, OS_TYPES } from 'src/helpers/window';
import { useIsMobile } from 'src/helpers/useIsMobile';
import {
  trackEmailConfirmedView,
  trackEmailConfirmedContinueClick,
  trackEmailConfirmedGooglePlayClick,
  trackEmailConfirmedAppStoreClick,
} from 'src/services/tracking/events/signup.events';

import mobileAppImage from 'src/assets/images/mobile-app.svg';
import logoTexto from 'src/assets/images/logo-texto.svg';
import signUpMobileBackground from 'src/assets/images/sign-up-mobile-background.png';
import { ReactComponent as AppStoreQRCode } from 'src/assets/images/app-store-qr-code.svg';
import { ReactComponent as PlayStoreQRCode } from 'src/assets/images/play-store-qr-code.svg';
import ArrowRightFilledIcon from 'src/components/Icons/ArrowIconFilledRight';
import AppleIcon from 'src/components/Icons/AppleIcon';
import GooglePlayIcon from 'src/components/Icons/GooglePlayIcon';
import { StoreCard } from './StoreCard';

export function EmailConfirmed() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const osType = useMemo(() => verifyOSSystem(), []);

  const showAppStore = !isMobile || osType === OS_TYPES.IOS || osType === OS_TYPES.UNKNOWN;
  const showPlayStore = !isMobile || osType === OS_TYPES.ANDROID || osType === OS_TYPES.UNKNOWN;

  const handleWhatsApp = () => {
    openWhatsApp(DEFAULT_MEVO_WHATSAPP_NUMBER());
  };

  const [searchParams] = useSearchParams();

  const userCreatedThroughApp = useMemo(() => {
    return searchParams.get('createdThroughApp') || false;
  }, [searchParams]);

  useEffect(() => {
    trackEmailConfirmedView();
  }, []);

  const handleNavigate = () => {
    trackEmailConfirmedContinueClick();
    if (userCreatedThroughApp) {
      openApp(() => {
        navigate('/');
      });
      return;
    }

    navigate('/');
  };

  const handleAppStoreClick = () => {
    trackEmailConfirmedAppStoreClick();
    openAppStore();
  };

  const handlePlayStoreClick = () => {
    trackEmailConfirmedGooglePlayClick();
    openPlayStore();
  };

  return (
    <Box
      w="100%"
      minH="100vh"
      bg="background"
      display="flex"
      flexDirection="column"
      overflow={{ base: 'auto', lg: 'hidden' }}
    >
      <Box p="0 20px" flexShrink={0}>
        <Header onGoBack={() => navigate('/')} />
      </Box>

      <Flex
        flex="1"
        align={{ base: 'flex-start', lg: 'center' }}
        justify="center"
        p={{ base: '20px', lg: '55px' }}
        pt={{ base: '0', lg: '0' }}
        pb={{ base: '40px', lg: '0' }}
      >
        <Flex
          display="flex"
          flexDirection={{ base: 'column', lg: 'row' }}
          gap={{ base: '20px', lg: '24px' }}
          h={{ base: 'auto', lg: 'min(calc(100vh - 180px), 50vw)' }}
          w="100%"
          maxH={{ base: 'none', lg: '700px' }}
          alignItems="center"
          justifyContent="center"
        >
          <Flex
            direction="column"
            bg={{ base: 'transparent', lg: 'white' }}
            borderRadius={{ base: '0', lg: '30px' }}
            boxShadow={{ base: 'none', lg: '12px 12px 40px rgba(61, 14, 107, 0.1)' }}
            p={{ base: '0', md: '32px', lg: 'clamp(24px, 3vw, 40px)' }}
            h={{ base: 'auto', lg: '100%' }}
            w={{ base: '100%', lg: 'auto' }}
            maxW={{ base: '100%', lg: '700px' }}
            flexShrink={0}
          >
            {/* Mobile: Icon at top */}
            <Flex
              display={{ base: 'flex', lg: 'none' }}
              w="120px"
              h="120px"
              borderRadius="50%"
              bg="secondary.100"
              align="center"
              justify="center"
              mx="auto"
              mb="32px"
            >
              <Box transform="scale(0.8)">
                <Allegril />
              </Box>
            </Flex>

            <Flex display={{ base: 'none', lg: 'flex' }} justify="space-between" align="flex-start" w="100%" mb="24px">
              <Box flex="1">
                <Box mb="24px">
                  <Box as="h1" fontSize="30px" fontWeight="500" color="#1E252B" lineHeight="1.2">
                    Email confirmado com sucesso!
                  </Box>
                </Box>

                <Box mb="8px">
                  <Box as="h3" fontSize="16px" fontWeight="400" color="#1E252B">
                    Leve a Mevo com você. Baixe o app
                  </Box>
                </Box>

                <Box>
                  <Typography model="body" size="large" weight="regular" color="gray.800">
                    Se houver qualquer dúvida, não se preocupe, é só{' '}
                    <b style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={handleWhatsApp}>
                      <span>falar com a gente</span>
                      <Flex display="inline-flex" alignItems="center" ml="4px">
                        <WhatsappIcon variantColor="gray.800" width={14} />
                      </Flex>
                    </b>
                  </Typography>
                </Box>
              </Box>

              <Flex
                w="100px"
                h="100px"
                borderRadius="50%"
                bg="secondary.100"
                align="center"
                justify="center"
                flexShrink={0}
                ml="24px"
              >
                <Box transform="scale(0.7)">
                  <Allegril />
                </Box>
              </Flex>
            </Flex>

            <Box display={{ base: 'block', lg: 'none' }}>
              <Box mb="24px">
                <Box as="h1" fontSize="30px" fontWeight="500" color="#1E252B" lineHeight="1.2" textAlign="left">
                  Email confirmado com sucesso!
                </Box>
              </Box>

              <Box mb="8px">
                <Box as="h3" fontSize="16px" fontWeight="400" color="#1E252B" textAlign="left">
                  Leve a Mevo com você. Baixe o app
                </Box>
              </Box>

              <Box textAlign="left">
                <Typography model="body" size="large" weight="regular" color="gray.800">
                  Se houver qualquer dúvida, não se preocupe, é só{' '}
                  <b style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={handleWhatsApp}>
                    <span>falar com a gente</span>
                    <Flex display="inline-flex" alignItems="center" ml="4px">
                      <WhatsappIcon variantColor="gray.800" width={14} />
                    </Flex>
                  </b>
                </Typography>
              </Box>
            </Box>

            {/* Section 2: QR Codes - Aligned Left */}
            <Flex mt="32px" justify="flex-start" w="100%">
              <Flex gap="16px" direction={{ base: 'column', sm: 'row' }} w={{ base: '100%', lg: 'auto' }}>
                {showAppStore && (
                  <StoreCard
                    storeName="Disponível na"
                    storeLabel="App Store"
                    icon={<AppleIcon width={20} />}
                    qrCode={<AppStoreQRCode width={90} height={90} />}
                    onClick={handleAppStoreClick}
                    isMobile={isMobile}
                  />
                )}
                {showPlayStore && (
                  <StoreCard
                    storeName="Disponível na"
                    storeLabel="Google Play"
                    icon={<GooglePlayIcon width={20} />}
                    qrCode={<PlayStoreQRCode width={90} height={90} />}
                    onClick={handlePlayStoreClick}
                    isMobile={isMobile}
                  />
                )}
              </Flex>
            </Flex>

            {/* Section 3: Button */}
            <Flex
              mt={{ base: '32px', lg: 'auto' }}
              pt={{ base: '0', lg: '32px' }}
              w="100%"
              justify={{ base: 'center', lg: 'flex-end' }}
            >
              <Button
                h="56px"
                w={{ base: '100%', lg: 'auto' }}
                px={{ base: '0', lg: '32px' }}
                onClick={handleNavigate}
                rightIcon={<ArrowRightFilledIcon width={14} variantColor="white" />}
              >
                Acessar
              </Button>
            </Flex>
          </Flex>

          <Flex
            flexDirection={'column'}
            display={{ base: 'none', lg: 'flex' }}
            backgroundImage={`url(${signUpMobileBackground})`}
            backgroundSize="cover"
            backgroundPosition="center"
            backgroundRepeat="no-repeat"
            borderRadius="30px"
            h="100%"
            w={{ base: '100%', lg: '560px' }}
            maxW="560px"
            overflow="hidden"
            align="center"
            justify="center"
          >
            <Image
              src={logoTexto}
              draggable="false"
              alt="Mevo Receita Digital"
              h="10%"
              maxW="100%"
              objectFit="contain"
            />
            <Image src={mobileAppImage} draggable="false" alt="Mevo App" h="80%" maxW="100%" objectFit="contain" />
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
}
